import { differenceInDays, isValid, parseISO } from 'date-fns';

import { AuthConfig } from '@/client/types/AuthConfig';
import { useConsentStore } from '@/client/services/state/consentStore';
import { useWidgetSessionStore } from '@/client/services/state/widgetSessionStore';

const NPS_ENDPOINT = 'https://wbauasrkycxfsyaotvfe.supabase.co/functions/v1/nps-submit';
const NPS_DECIDE_ENDPOINT = 'https://wbauasrkycxfsyaotvfe.supabase.co/functions/v1/nps-decide';
const NPS_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndiYXVhc3JreWN4ZnN5YW90dmZlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODA1OTA3NzksImV4cCI6MjA5NjE2Njc3OX0.xvDYAp-T2TObnGA04q_9qkEjQ3VR_VnhY1-1HHYLCog';
const NPS_DELAY_MS = 8000;
const SCRIPT_POLL_INTERVAL_MS = 200;
const SCRIPT_POLL_MAX_ATTEMPTS = 25;

// The widget rejects any audience outside this set and renders nothing.
export type NPSAudience = 'admin' | 'creator' | 'user';

export interface ContinuNPSConfig {
  enabled: boolean;
  audience: NPSAudience;
  userId: string;
  email?: string;
  accountId: string;
  accountName?: string;
  context?: { tenure_days?: number };
  endpoint: string;
  anonKey: string;
  decideEndpoint: string;
  delayMs: number;
}

const toAudience = (role: string | undefined): NPSAudience => {
  if (role === 'admin' || role === 'creator') {
    return role;
  }

  return 'user';
};

const daysSince = (isoDate: string | undefined): number | undefined => {
  const created = isoDate ? parseISO(isoDate) : undefined;

  if (!created || !isValid(created)) {
    return undefined;
  }

  return Math.max(0, differenceInDays(Date.now(), created));
};

// Stops the in-flight script poll, if any. `pollId` is passed by the poll
// itself so a poll that has already been superseded clears only its own timer
// and leaves its replacement's handle in the store.
const cancelScriptPoll = (pollId?: ReturnType<typeof setInterval>) => {
  const { npsScriptPollId, setNpsScriptPollId } = useWidgetSessionStore.getState();
  const target = pollId ?? npsScriptPollId;

  if (!target) {
    return;
  }

  clearInterval(target);

  if (target === npsScriptPollId) {
    setNpsScriptPollId(null);
  }
};

export const useNPS = () => {
  const addNPS = (authConfig: AuthConfig) => {
    if (!useConsentStore.getState().has('analytics')) {
      return;
    }

    const enabled = !!authConfig.company?.feature_flags?.enable_nps_surveys;
    if (!enabled) {
      return;
    }

    const { user, company } = authConfig;
    if (!user?._id || !company?._id) {
      return;
    }

    const identity = `${user._id}:${company._id}`;
    const { npsInitializedFor, setNpsInitializedFor, setNpsScriptPollId } =
      useWidgetSessionStore.getState();

    if (npsInitializedFor === identity) {
      return;
    }

    // A pending poll holds the previous identity's config in its closure and
    // would init with it after this one.
    cancelScriptPoll();

    // accountId must be the Planhat externalId, which is the company's Mongo
    // _id (Planhat addresses companies as `extid-<company._id>`).
    const initConfig: ContinuNPSConfig = {
      enabled,
      audience: toAudience(user.role),
      userId: user._id,
      email: user.email,
      accountId: company._id,
      accountName: company.name,
      context: {
        tenure_days: daysSince(company.createdAt),
      },
      endpoint: NPS_ENDPOINT,
      anonKey: NPS_ANON_KEY,
      decideEndpoint: NPS_DECIDE_ENDPOINT,
      delayMs: NPS_DELAY_MS,
    };

    setNpsInitializedFor(identity);

    let attempts = 0;
    const waitForScript = setInterval(() => {
      attempts += 1;

      if (window.ContinuNPS?.init) {
        cancelScriptPoll(waitForScript);
        window.ContinuNPS.init(initConfig);
        return;
      }

      if (attempts >= SCRIPT_POLL_MAX_ATTEMPTS) {
        cancelScriptPoll(waitForScript);
      }
    }, SCRIPT_POLL_INTERVAL_MS);

    setNpsScriptPollId(waitForScript);
  };

  const removeNPS = () => {
    // The ContinuNPS lib is loaded once via index.html and has no destroy API;
    // only the re-init guard is reset so the next session can init again.
    cancelScriptPoll();
    useWidgetSessionStore.getState().setNpsInitializedFor(null);
  };

  return {
    addNPS,
    removeNPS,
  };
};
